import { ISelectOption, IUIControlOption } from '@swissfex/sfx-commons';

import { PERIOD_UNIT_MONTHS_13 } from '../projects/edit-project/constants';
import { CivilStatus } from '../projects/models/enums/civilStatus';
import { Gender } from '../projects/models/enums/gender';
import { GiftAndInheritanceType } from '../projects/models/enums/giftAndInheritanceType';
import { HousingSituation } from '../projects/models/enums/housingSituation';
import { PensionType } from '../projects/models/enums/pensionType';
import { PeriodUnit } from '../projects/models/enums/periodUnit';
import { PropertyLaw } from '../projects/models/enums/propertyLaw';
import { ResidencePermit } from '../projects/models/enums/residencePermit';
import { TypeOfIncome } from '../projects/models/enums/typeOfIncome';
import { ExtendedPeriodUnit, YearAndMonths13PeriodUnit } from './types';

export const BOOLEAN_SELECT_OPTIONS: IUIControlOption<boolean>[] = [
    { value: true, label: 'global.yes' },
    { value: false, label: 'global.no' }
];

export const CIVIL_STATUS_OPTIONS: ISelectOption<CivilStatus>[] = [
    { value: CivilStatus.Single, label: `edit_borrower.personal_details.civil_status.${CivilStatus.Single}` },
    { value: CivilStatus.Married, label: `edit_borrower.personal_details.civil_status.${CivilStatus.Married}` },
    {
        value: CivilStatus.InRegisteredPartnership,
        label: `edit_borrower.personal_details.civil_status.${CivilStatus.InRegisteredPartnership}`
    },
    { value: CivilStatus.Divorced, label: `edit_borrower.personal_details.civil_status.${CivilStatus.Divorced}` },
    { value: CivilStatus.Separated, label: `edit_borrower.personal_details.civil_status.${CivilStatus.Separated}` },
    { value: CivilStatus.Widowed, label: `edit_borrower.personal_details.civil_status.${CivilStatus.Widowed}` }
];

export const PROPERTY_LAW_OPTIONS: ISelectOption<PropertyLaw>[] = [
    {
        value: PropertyLaw.CommunityOfAcquisitions,
        label: `edit_borrower.personal_details.property_law.${PropertyLaw.CommunityOfAcquisitions}`
    },
    {
        value: PropertyLaw.SeparationOfProperty,
        label: `edit_borrower.personal_details.property_law.${PropertyLaw.SeparationOfProperty}`
    },
    {
        value: PropertyLaw.CommunityOfProperty,
        label: `edit_borrower.personal_details.property_law.${PropertyLaw.CommunityOfProperty}`
    },
    { value: PropertyLaw.Contract, label: `edit_borrower.personal_details.property_law.${PropertyLaw.Contract}` },
    { value: PropertyLaw.NotKnown, label: `edit_borrower.personal_details.property_law.${PropertyLaw.NotKnown}` }
];

export const GENDER_TITLE_SELECT_OPTIONS: IUIControlOption<Gender>[] = [
    { value: Gender.Male, label: 'edit_borrower.personal_details.gender_male' },
    { value: Gender.Female, label: 'edit_borrower.personal_details.gender_female' }
];

export const PENSION_TYPE_OPTIONS: ISelectOption<PensionType>[] = [
    {
        value: PensionType.Pillar,
        label: `collateral.pension_type.${PensionType.Pillar}`
    },
    {
        value: PensionType.VestedBenefitsPolicy,
        label: `collateral.pension_type.${PensionType.VestedBenefitsPolicy}`
    },
    {
        value: PensionType.VestedBenefitsAccount,
        label: `collateral.pension_type.${PensionType.VestedBenefitsAccount}`
    },
    {
        value: PensionType.BankBalances,
        label: `collateral.pension_type.${PensionType.BankBalances}`
    },
    {
        value: PensionType.ClassicPolicy3A,
        label: `collateral.pension_type.${PensionType.ClassicPolicy3A}`
    },
    {
        value: PensionType.FundPolicyWithGuarantee3A,
        label: `collateral.pension_type.${PensionType.FundPolicyWithGuarantee3A}`
    },
    {
        value: PensionType.FundPolicyWithoutGuarantee3A,
        label: `collateral.pension_type.${PensionType.FundPolicyWithoutGuarantee3A}`
    },
    {
        value: PensionType.ClassicPolicy3B,
        label: `collateral.pension_type.${PensionType.ClassicPolicy3B}`
    },
    {
        value: PensionType.FundPolicyWithGuarantee3B,
        label: `collateral.pension_type.${PensionType.FundPolicyWithGuarantee3B}`
    },
    {
        value: PensionType.FundPolicyWithoutGuarantee3B,
        label: `collateral.pension_type.${PensionType.FundPolicyWithoutGuarantee3B}`
    }
];

export const PENSION_TYPE_OPTIONS_3B: ISelectOption<PensionType>[] = [
    {
        value: PensionType.ClassicPolicy3B,
        label: `collateral.pension_type.${PensionType.ClassicPolicy3B}`
    },
    {
        value: PensionType.FundPolicyWithGuarantee3B,
        label: `collateral.pension_type.${PensionType.FundPolicyWithGuarantee3B}`
    },
    {
        value: PensionType.FundPolicyWithoutGuarantee3B,
        label: `collateral.pension_type.${PensionType.FundPolicyWithoutGuarantee3B}`
    }
];

export const RESIDENCE_PERMIT_OPTIONS: ISelectOption<ResidencePermit>[] = [
    {
        value: ResidencePermit.PermanentResidencePermitC,
        label: 'edit_borrower.personal_details.residence_permit_options.' + ResidencePermit.PermanentResidencePermitC
    },
    {
        value: ResidencePermit.ResidencePermitB,
        label: 'edit_borrower.personal_details.residence_permit_options.' + ResidencePermit.ResidencePermitB
    },
    {
        value: ResidencePermit.AsylumSeekersN,
        label: 'edit_borrower.personal_details.residence_permit_options.' + ResidencePermit.AsylumSeekersN
    },
    {
        value: ResidencePermit.ProvisionallyAdmittedForeigner,
        label:
            'edit_borrower.personal_details.residence_permit_options.' + ResidencePermit.ProvisionallyAdmittedForeigner
    },
    {
        value: ResidencePermit.ShortTermResidencePermitL,
        label: 'edit_borrower.personal_details.residence_permit_options.' + ResidencePermit.ShortTermResidencePermitL
    },
    {
        value: ResidencePermit.PeopleInNeedOfProtectionS,
        label: 'edit_borrower.personal_details.residence_permit_options.' + ResidencePermit.PeopleInNeedOfProtectionS
    },
    {
        value: ResidencePermit.CrossBorderCommuterG,
        label: 'edit_borrower.personal_details.residence_permit_options.' + ResidencePermit.CrossBorderCommuterG
    }
];

export const TYPE_OF_INCOME_OPTIONS: ISelectOption<TypeOfIncome>[] = [
    {
        value: TypeOfIncome.Employed,
        label: `edit_borrower.employment_type.${TypeOfIncome.Employed}`
    },
    {
        value: TypeOfIncome.PaidPerHour,
        label: `edit_borrower.employment_type.${TypeOfIncome.PaidPerHour}`
    },
    {
        value: TypeOfIncome.EmployedInOwnCompany,
        label: `edit_borrower.employment_type.${TypeOfIncome.EmployedInOwnCompany}`
    },
    {
        value: TypeOfIncome.SelfEmployed,
        label: `edit_borrower.employment_type.${TypeOfIncome.SelfEmployed}`
    },
    {
        value: TypeOfIncome.Unemployed,
        label: `edit_borrower.employment_type.${TypeOfIncome.Unemployed}`
    },
    {
        value: TypeOfIncome.Pensioner,
        label: `edit_borrower.employment_type.${TypeOfIncome.Pensioner}`
    }
];

export const SECONDARY_EMPLOYMENT_TYPE_OF_INCOME_OPTIONS: ISelectOption<TypeOfIncome>[] = [
    {
        value: TypeOfIncome.Employed,
        label: `edit_borrower.employment_type.${TypeOfIncome.Employed}`
    },
    {
        value: TypeOfIncome.PaidPerHour,
        label: `edit_borrower.employment_type.${TypeOfIncome.PaidPerHour}`
    },
    {
        value: TypeOfIncome.EmployedInOwnCompany,
        label: `edit_borrower.employment_type.${TypeOfIncome.EmployedInOwnCompany}`
    },
    {
        value: TypeOfIncome.SelfEmployed,
        label: `edit_borrower.employment_type.${TypeOfIncome.SelfEmployed}`
    }
];

export const PERIOD_UNIT_OPTIONS: ISelectOption<PeriodUnit>[] = [
    {
        value: PeriodUnit.Year,
        label: 'global.per_year'
    },
    {
        value: PeriodUnit.Month,
        label: 'global.per_month'
    }
];

export const ANNUALIZED_PERIOD_UNIT_OPTIONS: ISelectOption<ExtendedPeriodUnit>[] = [
    {
        value: PeriodUnit.Year,
        label: 'global.per_year'
    },
    {
        value: PeriodUnit.Month,
        label: 'global.per_month_x_12'
    },
    {
        value: PERIOD_UNIT_MONTHS_13,
        // Used as a title.
        label: 'global.per_month_x_13',
        customLabel: 'global.per_month_x_13_html'
    }
];

export const YEAR_AND_MONTHS13_PERIOD_UNIT_OPTIONS: ISelectOption<YearAndMonths13PeriodUnit>[] = [
    {
        value: PeriodUnit.Year,
        label: 'global.per_year'
    },
    {
        value: PERIOD_UNIT_MONTHS_13,
        // Used as a title.
        label: 'global.per_month_x_13',
        customLabel: 'global.per_month_x_13_html'
    }
];

export const HOUSING_SITUATION_OPTIONS: { label: string; value: HousingSituation }[] = [
    {
        label: `edit_borrower.liabilities.housing_situation.${HousingSituation.Residence}`,
        value: HousingSituation.Residence
    },
    {
        label: `edit_borrower.liabilities.housing_situation.${HousingSituation.RentedProperty}`,
        value: HousingSituation.RentedProperty
    },
    {
        label: `edit_borrower.liabilities.housing_situation.${HousingSituation.WithOtherBorrower}`,
        value: HousingSituation.WithOtherBorrower
    }
];

export const GIFT_AND_ADVANCED_INHERITANCE_OPTIONS: ISelectOption[] = [
    {
        label: `edit_financing.gifts_or_advance_inheritances.${GiftAndInheritanceType.Gift}`,
        value: GiftAndInheritanceType.Gift
    },
    {
        label: `edit_financing.gifts_or_advance_inheritances.${GiftAndInheritanceType.AdvanceOfInheritance}`,
        value: GiftAndInheritanceType.AdvanceOfInheritance
    }
];
